import { useCallback, useEffect, useState } from "react";
import { Api } from "@lib/api";

// Type definition for Zebra BrowserPrint device
export interface BrowserPrintDevice {
  name: string;
  deviceType: string;
  connection: string;
  uid: string;
  version: number;
  provider: string;
  manufacturer: string;
  readRetries: number;
  send: (
    dataToWrite: string,
    successCallback?: () => void,
    errorCallback?: () => void,
  ) => void;
  // Other methods/properties can be added as needed
}

// Declare BrowserPrint as a global variable if provided by an external script
declare const BrowserPrint: {
  getDefaultDevice: (
    type: string,
    successCallback: (device: BrowserPrintDevice) => void,
    errorCallback: () => void,
  ) => void;
  // Add other methods as needed
};

export function usePrint() {
  // Get the default printer device

  const [defaultDevice, setDefaultDevice] = useState<BrowserPrintDevice | null>(
    null,
  );

  const getDefaultPrinterDevice =
    useCallback((): Promise<BrowserPrintDevice> => {
      return new Promise((resolve, reject) => {
        if (typeof BrowserPrint === "undefined") {
          reject(
            new Error(
              "BrowserPrint is not available. Please ensure the script is included.",
            ),
          );
          return;
        }
        BrowserPrint.getDefaultDevice(
          "printer",
          (device: BrowserPrintDevice) => resolve(device),
          () => reject("Error getting default printer device"),
        );
      });
    }, []);

  // Send data to the printer
  const sendToPrinter = useCallback(
    (device: BrowserPrintDevice, data: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        device.send(
          data,
          () => resolve(),
          () => reject("Error sending data to printer"),
        );
      });
    },
    [],
  );

  // Fetch pocket data
  const fetchPocket = useCallback(async (pocketId: string) => {
    try {
      const response = await Api.get(`/collect/${pocketId}/tickets`);
      return response.pocket;
    } catch (error) {
      console.error("Error fetching pocket for printing:", error);
      throw error;
    }
  }, []);

  // Print a pocket by id
  const printPocket = useCallback(
    async (pocketId: string) => {
      if (!defaultDevice) {
        alert(
          "No printer device found. Please set up a printer to enable printing.",
        );
        return;
      }
      try {
        const pocketData = await fetchPocket(pocketId);
        const printData = generateZPLFromPocket(pocketData);
        await sendToPrinter(defaultDevice, printData);
      } catch (error: unknown) {
        alert(`Error during printing: ${error}`);
      }
    },
    [defaultDevice, fetchPocket, sendToPrinter],
  );

  const generateZPLFromPocket = (pocketData: any): string => {
    return pocketData.tickets
      .map((ticket: any) => {
        const eventName = ticket.event.name;
        const ticketType = ticket.type.name;
        const qrBarcode = ticket.barcode;

        return `^XA
        // QR Code
        ^FO10,50^BQN,2,7^FDLA,${qrBarcode}^FS

        // Event Naam (Multi-line)
        // ^FB[breedte],[max lijnen],[ruimte tussen lijnen],[uitlijning],[marge]
        ^FO240,65^A0N,25,25^FB450,2,0,L^FD${eventName}^FS

        // Ticket Type (Multi-line)
        // We zetten deze iets lager (y=135) om ruimte te geven aan de event naam
        ^FO240,135^A0N,40,40^FB450,2,0,L^FD${ticketType}^FS
        ^XZ`;
      })
      .join("");
  };

  useEffect(() => {
    const initializePrinter = async () => {
      try {
        if (typeof BrowserPrint === "undefined") {
          console.warn(
            "BrowserPrint is not available. Please ensure the script is included.",
          );
          return;
        }
        const device: BrowserPrintDevice = await getDefaultPrinterDevice();
        if (device.uid) {
          setDefaultDevice(device);
        }
      } catch (error) {
        console.error("Error initializing printer device:", error);
      }
    };

    initializePrinter();
  }, [getDefaultPrinterDevice]);

  return {
    hasPrinter: !!defaultDevice,
    getDefaultPrinterDevice,
    printPocket,
  };
}
